import { useState, useEffect } from 'react';
import { collection, getDocs, getDoc, deleteDoc, doc, updateDoc, setDoc, query, where } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShield, faUserPlus, faTrash, faEdit, faUserShield, faUserTie, faChartLine } from '@fortawesome/free-solid-svg-icons';

const ROLES = {
  super_admin: { label: 'Super Admin', icon: faUserShield, color: 'text-red-600' },
  content_moderator: { label: 'Content Moderator', icon: faUserTie, color: 'text-blue-600' },
  analyst: { label: 'Analyst', icon: faChartLine, color: 'text-green-600' }
};

export default function AdminRoleManager() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminName, setNewAdminName] = useState('');
  const [newAdminRole, setNewAdminRole] = useState('content_moderator');

  useEffect(() => {
    loadAdmins();
  }, []);

  async function loadAdmins() {
    setLoading(true);
    try {
      const adminsSnapshot = await getDocs(collection(db, 'admins'));
      const adminsList = [];
      
      for (const adminDoc of adminsSnapshot.docs) {
        const adminData = adminDoc.data();
        // Try to get user info
        try {
          const userDoc = await getDocs(query(collection(db, 'users'), where('email', '==', adminData.Email || '')));
          if (!userDoc.empty) {
            const userData = userDoc.docs[0].data();
            adminsList.push({
              id: adminDoc.id,
              ...adminData,
              userId: adminDoc.id,
              displayName: userData.displayName || adminData.Name || 'Unknown',
              email: adminData.Email || userData.email || 'Unknown'
            });
          } else {
            adminsList.push({
              id: adminDoc.id,
              ...adminData,
              userId: adminDoc.id,
              displayName: adminData.Name || 'Unknown',
              email: adminData.Email || 'Unknown'
            });
          }
        } catch (error) {
          adminsList.push({
            id: adminDoc.id,
            ...adminData,
            userId: adminDoc.id,
            displayName: adminData.Name || 'Unknown',
            email: adminData.Email || 'Unknown'
          });
        }
      }
      
      setAdmins(adminsList);
      setLoading(false);
    } catch (error) {
      console.error('Error loading admins:', error);
      toast.error('Failed to load admins');
      setLoading(false);
    }
  }

  async function handleAddAdmin() {
    if (!newAdminEmail || !newAdminName) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      // Find user by email to get their UID
      const usersQuery = query(collection(db, 'users'), where('email', '==', newAdminEmail));
      const usersSnapshot = await getDocs(usersQuery);
      
      if (usersSnapshot.empty) {
        toast.error('User with this email not found. They must sign up first.');
        return;
      }

      const userId = usersSnapshot.docs[0].id;
      
      // Check if already an admin
      const adminDocRef = doc(db, 'admins', userId);
      const adminCheck = await getDoc(adminDocRef);
      if (adminCheck.exists()) {
        toast.error('This user is already an admin');
        return;
      }

      // Add admin (using userId as document ID)
      await setDoc(adminDocRef, {
        Email: newAdminEmail,
        Name: newAdminName,
        role: newAdminRole,
        createdAt: new Date(),
        createdBy: user.uid
      });

      toast.success('Admin added successfully');
      setShowAddModal(false);
      setNewAdminEmail('');
      setNewAdminName('');
      setNewAdminRole('content_moderator');
      loadAdmins();
    } catch (error) {
      console.error('Error adding admin:', error);
      toast.error('Failed to add admin');
    }
  }

  async function handleUpdateRole(adminId, newRole) {
    try {
      await updateDoc(doc(db, 'admins', adminId), {
        role: newRole
      });
      toast.success('Role updated successfully');
      loadAdmins();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Failed to update role');
    }
  }

  async function handleRemoveAdmin(adminId) {
    if (!window.confirm('Are you sure you want to remove this admin?')) return;
    
    try {
      await deleteDoc(doc(db, 'admins', adminId));
      toast.success('Admin removed successfully');
      loadAdmins();
    } catch (error) {
      console.error('Error removing admin:', error);
      toast.error('Failed to remove admin');
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-serif font-bold text-text-primary flex items-center gap-3">
                <FontAwesomeIcon icon={faShield} className="text-4xl text-primary" />
                Admin Role Management
              </h1>
              <p className="text-text-secondary mt-1">
                Manage admin users and their permissions
              </p>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-gradient-to-r from-primary to-primary-light text-white font-semibold px-6 py-3 rounded-xl hover:shadow-lg transition-all flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faUserPlus} />
              Add Admin
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-8">
        <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-border">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Name</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Email</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Role</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {admins.map(admin => (
                <tr key={admin.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-text-primary">{admin.displayName}</td>
                  <td className="px-6 py-4 text-sm text-text-secondary">{admin.email}</td>
                  <td className="px-6 py-4">
                    <select
                      value={admin.role || 'content_moderator'}
                      onChange={(e) => handleUpdateRole(admin.id, e.target.value)}
                      className={`px-3 py-1 rounded-lg border border-border text-sm font-semibold ${ROLES[admin.role || 'content_moderator']?.color || 'text-gray-600'}`}
                    >
                      {Object.entries(ROLES).map(([key, role]) => (
                        <option key={key} value={key}>{role.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleRemoveAdmin(admin.id)}
                      className="text-red-600 hover:text-red-700 transition-colors"
                      disabled={admin.id === user?.uid}
                      title={admin.id === user?.uid ? 'Cannot remove yourself' : 'Remove admin'}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Admin Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <h2 className="text-2xl font-serif font-bold text-text-primary mb-4">Add New Admin</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">Name</label>
                <input
                  type="text"
                  value={newAdminName}
                  onChange={(e) => setNewAdminName(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
                  placeholder="Admin name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">Email</label>
                <input
                  type="email"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
                  placeholder="user@example.com"
                />
                <p className="text-xs text-text-secondary mt-1">User must have signed up first</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">Role</label>
                <select
                  value={newAdminRole}
                  onChange={(e) => setNewAdminRole(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
                >
                  {Object.entries(ROLES).map(([key, role]) => (
                    <option key={key} value={key}>{role.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewAdminEmail('');
                  setNewAdminName('');
                }}
                className="flex-1 bg-gray-100 text-text-primary font-semibold py-3 rounded-xl hover:bg-gray-200 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleAddAdmin}
                className="flex-1 bg-gradient-to-r from-primary to-primary-light text-white font-semibold py-3 rounded-xl hover:shadow-lg transition-all"
              >
                Add Admin
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

